import React from 'react';
import './style.css';

const getHref = (button) => {
  // If there's an anchor, use it (with or without a base link)
  if (button.anchor) {
    const link = Array.isArray(button.link) ? button.link[0] : button.link;
    const baseUrl = link?.['@id'] || '';
    const isFile = link?.['@type'] === 'File';
    const finalUrl = isFile ? `${baseUrl}/@@download/file` : baseUrl;
    
    // Combine URL and anchor, or just use anchor
    return finalUrl ? `${finalUrl}#${button.anchor}` : `#${button.anchor}`;
  }
  
  // No anchor, use existing link logic
  const item = Array.isArray(button.link) ? button.link[0] : button.link;
  if (!item) return '#';
  const url = item['@id'] || '#';
  const isFile = item['@type'] === 'File';
  return isFile ? `${url}/@@download/file` : url;
};

const extractTOCEntriesInSection = (blockId, blocks, layout, data = {}) => {
  const allowH2 = data.useH2 ?? true;
  const allowH3 = data.useH3 ?? true;
  const allowedTypes = [
    ...(allowH2 ? ['heading-two', 'h2'] : []),
    ...(allowH3 ? ['heading-three', 'h3'] : []),
  ];

  const startIndex = layout.indexOf(blockId);
  const nextSameBlock = layout
    .slice(startIndex + 1)
    .find((id) => blocks[id]?.['@type'] === 'buttonRow');
  const endIndex = nextSameBlock
    ? layout.indexOf(nextSameBlock)
    : layout.length;
  const targetIds = layout.slice(startIndex + 1, endIndex);

  const entries = [];

  targetIds.forEach((id) => {
    const block = blocks[id];
    if (
      (block?.['@type'] === 'text' || block?.['@type'] === 'slate') &&
      Array.isArray(block.value)
    ) {
      block.value.forEach((node) => {
        if (allowedTypes.includes(node.type)) {
          const text = node.children?.map((c) => c.text).join('').trim();
          const anchor = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
          if (text) entries.push({ id: anchor, title: text });
        }
      });
    }
  });

  return entries;
};

const View = ({ data = {}, properties = {}, block }) => {
  const image = data.backgroundImage?.[0];
  const alignment = data.alignment || 'left';
  const buttonsPosition = data.buttonsPosition || 'below';
  const imageUrl = image?.['@id'] ? `${image['@id']}/@@images/image` : '';
  const backgroundColor = data.backgroundColor || '#f5f5f5';
  const displayAsList = data.displayAsList === true;

  const colorLabelMap = {
    '#000000': 'black',
    '#ffffff': 'white',
    '#f5f5f5': 'light-grey',
    '#ffcc00': 'yellow',
    '#006699': 'blue',
  };

  const colorLabel = (colorLabelMap[backgroundColor] || backgroundColor)
    .replace(/[^a-z0-9]+/gi, '-')
    .toLowerCase();
  const noBg = !imageUrl;
  const darkButtons =
    noBg && ['#ffffff', '#f5f5f5', '#ffcc00'].includes(backgroundColor);

  const wrapperStyle = displayAsList ? {} : {
    backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
    backgroundColor,
  };

  const customClass = data.customClass || '';
  const wrapperClass = displayAsList 
    ? `button-row-list-mode${customClass ? ` ${customClass}` : ''}` 
    : `button-row-block ${data.isFullWidth ? 'full-width' : ''} ${noBg ? 'no-bg' : ''} ${darkButtons ? 'dark-buttons' : ''} color-${colorLabel}${customClass ? ` ${customClass}` : ''}`;

  const layout = properties.blocks_layout?.items || [];
  const blocks = properties.blocks || {};
  const tocEntries = data.useTOC ? extractTOCEntriesInSection(block, blocks, layout, data) : [];

  const renderButtons = (buttons, isListMode = false) => {
    if (isListMode) {
      return (
        <div className="button-row-list">
          <p>
            {buttons.map((btn, i) => (
              <React.Fragment key={i}>
                {(() => {
                  const href = getHref(btn);
                  const isExternal = href?.startsWith('http');
                  return (
                    <a
                      href={href}
                      {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                      {btn.label}
                    </a>
                  );
                })()}
                {i < buttons.length - 1 && ' | '}
              </React.Fragment>
            ))}
          </p>
        </div>
      );
    }

    return (
      <div
        className={`button-row-buttons${data.smallButtons ? ' small-buttons' : ''}`}
      >
        {buttons.map((btn, i) => {
          const href = getHref(btn);
          const isExternal = href?.startsWith('http');
          const buttonStyle = btn.buttonStyle || 'default';
          const buttonClass = `row-button button-style-${buttonStyle}`;
          
          return (
            <a
              key={i}
              href={href}
              className={buttonClass}
              {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {btn.label}
            </a>
          );
        })}
      </div>
    );
  };

  const renderTOCButtons = (isListMode = false) => {
    if (isListMode) {
      return (
        <div className="button-row-list">
          <p>
            {tocEntries.map((entry, i) => (
              <React.Fragment key={i}>
                <a href={`#${entry.id}`}>{entry.title}</a>
                {i < tocEntries.length - 1 && ' | '}
              </React.Fragment>
            ))}
          </p>
        </div>
      );
    }

    return (
      <div
        className={`button-row-buttons${data.smallButtons ? ' small-buttons' : ''}`}
      >
        {tocEntries.map((entry, i) => (
          <a key={i} href={`#${entry.id}`} className="row-button">
            {entry.title}
          </a>
        ))}
      </div>
    );
  };

  const contentSection = (
    <div className="button-row-content">
      {data.preHeader && <div className="pre-header">{data.preHeader}</div>}
      {data.title && <h1>{data.title}</h1>}
      {data.text && <p>{data.text}</p>}
    </div>
  );

  const buttonsSection = data.useTOC 
    ? renderTOCButtons(displayAsList)
    : (data.buttons?.length > 0 && renderButtons(data.buttons, displayAsList));

  return (
    <div className={wrapperClass} style={wrapperStyle}>
      {!displayAsList && imageUrl && <div className="overlay" />}
      <div className={displayAsList ? '' : `button-row-inner align-${alignment} buttons-${buttonsPosition}`}>
        {buttonsPosition === 'beside' && !displayAsList ? (
          <div className="button-row-flex-container">
            {contentSection}
            {buttonsSection}
          </div>
        ) : (
          <>
            {contentSection}
            {buttonsSection}
          </>
        )}
      </div>
    </div>
  );
};

export default View;
